import React, { useState, useEffect, useRef, useMemo } from 'react';
import { addDaysYMD } from '../lib/utils';
import { scheduleApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Pencil, Trash2,
  Truck, Phone, GripVertical, User, Package, MapPin, Copy, PanelLeftClose, PanelLeft,
  Printer, CalendarOff, Share2,
} from 'lucide-react';
import { toast } from 'sonner';

/* ── Colours ── */
const CL = [
  { bg: '#FDBA74', text: '#7C2D12', border: '#FB923C' }, // orange
  { bg: '#93C5FD', text: '#1E3A5F', border: '#60A5FA' }, // blue
  { bg: '#6EE7B7', text: '#064E3B', border: '#34D399' }, // emerald
  { bg: '#C4B5FD', text: '#3B0764', border: '#A78BFA' }, // purple
  { bg: '#FDA4AF', text: '#881337', border: '#FB7185' }, // rose
  { bg: '#FCD34D', text: '#78350F', border: '#FBBF24' }, // amber
  { bg: '#67E8F9', text: '#164E63', border: '#22D3EE' }, // cyan
  { bg: '#A5B4FC', text: '#312E81', border: '#818CF8' }, // indigo
];
const LOAD_CL = { bg: '#99F6E4', text: '#134E4A', border: '#2DD4BF' };
const TIMEOFF_CL = { bg: '#FCA5A5', text: '#7F1D1D', border: '#EF4444' };

/* ── Date helpers (timezone-safe, uses local date strings) ── */
function localToday() {
  // Eastern time: UTC-5 (EST) or UTC-4 (EDT)
  const now = new Date();
  const eastern = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return toYMD(eastern);
}
function toYMD(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function parseD(s) { const [y,m,d] = s.split('-').map(Number); return new Date(y, m-1, d); } // local midnight, no TZ issues
function addDays(s, n) { const d = parseD(s); d.setDate(d.getDate()+n); return toYMD(d); }
function daysBetween(a, b) { return Math.round((parseD(b) - parseD(a)) / 864e5); }
function getSunday(ymd) { const d = parseD(ymd); d.setDate(d.getDate() - d.getDay()); return toYMD(d); }
function fmtShort(ymd) { const d = parseD(ymd); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
function wkSpan(s, e) { return Math.max(1, Math.ceil((daysBetween(s, e) + 1) / 7)); }

function genWeeks(startYMD, n) {
  const w = [];
  for (let i = 0; i < n; i++) {
    const s = addDays(startYMD, i * 7);
    const e = addDays(s, 6);
    w.push({ start: s, end: e });
  }
  return w;
}

const DW = 46; // day width px
const DNAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const RH = 78; // row height

export default function Schedule() {
  const [drivers, setDrivers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [dialogTab, setDialogTab] = useState('contract');
  const [viewWeeks, setViewWeeks] = useState(8);
  const [showVacancies, setShowVacancies] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [focusedDriver, setFocusedDriver] = useState(null);
  const [driverFilter, setDriverFilter] = useState('active');
  const [viewStart, setViewStart] = useState(() => addDays(getSunday(localToday()), -7));
  const [formData, setFormData] = useState({ driver_id:'', entry_type:'contract', contract_id:'', location:'', start_date:'', end_date:'', block_pay:'', weekly_total:'', blocks_per_week:'3', tractors:'1', tractor_drivers:[''], notes:'' });
  // Drag state is tracked via refs only (no React state) — calling setState during
  // dragstart causes Chromium to cancel the native drag session due to mid-render
  // reconciliation of the dragged element.
  const dragEntryRef = useRef(null);
  const dragSegmentRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    try { const [dr,en] = await Promise.all([scheduleApi.getDrivers(true), scheduleApi.getEntries()]); setDrivers(dr.data); setEntries(en.data); }
    catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!loading && scrollRef.current) {
      const off = daysBetween(viewStart, localToday());
      scrollRef.current.scrollLeft = Math.max(0, off * DW - 200);
    }
  }, [loading, viewStart]);

  const weeks = useMemo(() => genWeeks(viewStart, viewWeeks), [viewStart, viewWeeks]);
  const totalDays = viewWeeks * 7;
  const driverMap = useMemo(() => Object.fromEntries(drivers.map(d=>[d.id,d])), [drivers]);
  const unassigned = useMemo(() => entries.filter(e=>!e.driver_id), [entries]);
  const visibleDrivers = useMemo(() => drivers.filter(d => (
    driverFilter === 'all' ? true : driverFilter === 'active' ? d.status === 'active' : d.status !== 'active'
  )), [drivers, driverFilter]);
  const inactiveCount = drivers.filter(d => d.status !== 'active').length;

  /* ── Contract-chain detection ──
   * Group consecutive entries on the same driver that share block rate/contract id/location.
   * Each entry gets metadata: {chainLength, weekIndex, chainTotalWeeks} so we can render
   * a "Week 2 of 4" badge for any week-slice that came out of a multi-week contract split.
   */
  const chainMap = useMemo(() => {
    const map = new Map();
    if (!entries.length) return map;

    // Bucket by driver_id + contract identity. Use contract_id when present, otherwise
    // fall back to a synthetic key built from rate + location + blocks_per_week.
    const buckets = new Map();
    for (const e of entries) {
      if (e.entry_type !== 'contract') continue;
      const idKey = e.contract_id ||
        `__nc__|${e.block_pay || 0}|${e.location || ''}|${e.blocks_per_week || 3}`;
      const k = `${e.driver_id || 'unassigned'}|${idKey}`;
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(e);
    }

    const addDays = (d, n) => addDaysYMD(d, n);
    const wkSpanLocal = (s, e) => {
      const ds = new Date(s + 'T00:00:00');
      const de = new Date(e + 'T00:00:00');
      return Math.max(1, Math.round((de - ds) / 86400000 / 7) + (((de - ds) / 86400000) % 7 ? 1 : 0));
    };

    for (const list of buckets.values()) {
      list.sort((a, b) => a.start_date.localeCompare(b.start_date));

      // Walk the sorted list, building chains where each entry's start_date is contiguous
      // with the previous entry's end_date + 1 day.
      let chain = [];
      const flush = () => {
        if (!chain.length) return;
        const totalWks = chain.reduce((s, x) => s + wkSpanLocal(x.start_date, x.end_date), 0);
        let runningWk = 0;
        for (const x of chain) {
          const xWks = wkSpanLocal(x.start_date, x.end_date);
          map.set(x.id, {
            chainLength: chain.length,
            chainTotalWeeks: totalWks,
            weekStartIndex: runningWk + 1,   // 1-based
            weekEndIndex: runningWk + xWks,
          });
          runningWk += xWks;
        }
        chain = [];
      };
      for (const e of list) {
        if (chain.length && chain[chain.length - 1].end_date !== addDays(e.start_date, -1)) {
          flush();
        }
        chain.push(e);
      }
      flush();
    }
    return map;
  }, [entries]);
  const todayYMD = localToday();

  // Check if a driver has a scheduling conflict for given date range
  // Returns the conflicting entry or null
  const getConflict = (driverId, startDate, endDate, excludeEntryId = null) => {
    if (!driverId || !startDate || !endDate) return null;
    return entries.find(e => {
      if (!e.driver_id || e.driver_id !== driverId) return false;
      if (excludeEntryId && e.id === excludeEntryId) return false;
      // Date overlap check: e.start <= endDate && e.end >= startDate
      return e.start_date <= endDate && e.end_date >= startDate;
    }) || null;
  };

  // Get list of available drivers for a date range (no conflicts)
  const getDriverAvailability = (startDate, endDate, excludeEntryId = null) => {
    return drivers.map(d => {
      const conflict = getConflict(d.id, startDate, endDate, excludeEntryId);
      return { ...d, conflict };
    });
  };

  /* ── Handlers ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const isLoad = (editingEntry?.entry_type==='load') || dialogTab==='load';
    const isTimeoff = (editingEntry?.entry_type==='timeoff') || dialogTab==='timeoff';
    const entryType = isTimeoff ? 'timeoff' : (isLoad ? 'load' : 'contract');
    const numTractors = entryType === 'contract' ? (parseInt(formData.tractors) || 1) : 1;
    const blockPay = parseFloat(formData.block_pay) || 0;

    try {
      if (editingEntry) {
        // Edit: check conflict for new driver assignment
        if (formData.driver_id) {
          const conflict = getConflict(formData.driver_id, formData.start_date, formData.end_date, editingEntry.id);
          if (conflict) {
            const cLabel = conflict.contract_id || conflict.notes || conflict.entry_type;
            toast.error(`${driverMap[formData.driver_id]?.name || 'Driver'} is unavailable — conflicts with "${cLabel}" (${conflict.start_date} to ${conflict.end_date})`);
            return;
          }
        }
        const payload = { driver_id: formData.driver_id||null, entry_type: entryType, contract_id: entryType==='contract'?(formData.contract_id||null):null, location: entryType==='contract'?(formData.location||null):null, start_date: formData.start_date, end_date: formData.end_date, block_pay: entryType==='contract'?blockPay:0, blocks_per_week: entryType==='contract'?(parseInt(formData.blocks_per_week)||3):1, notes: formData.notes||null };
        await scheduleApi.update(editingEntry.id, payload);
        toast.success('Updated');
      } else if (numTractors > 1 && entryType === 'contract') {
        // Multi-tractor: check duplicates and conflicts for each assigned driver
        const seen = new Set();
        for (let i = 0; i < numTractors; i++) {
          const did = formData.tractor_drivers[i];
          if (did) {
            if (seen.has(did)) {
              toast.error(`${driverMap[did]?.name || 'Driver'} is assigned to multiple trucks — pick a different driver`);
              return;
            }
            seen.add(did);
            const conflict = getConflict(did, formData.start_date, formData.end_date);
            if (conflict) {
              const cLabel = conflict.contract_id || conflict.notes || conflict.entry_type;
              toast.error(`Truck ${i+1}: ${driverMap[did]?.name || 'Driver'} conflicts with "${cLabel}" (${conflict.start_date} to ${conflict.end_date})`);
              return;
            }
          }
        }
        for (let i = 0; i < numTractors; i++) {
          const driverId = formData.tractor_drivers[i] || null;
          const payload = { driver_id: driverId||null, entry_type: 'contract', contract_id: formData.contract_id||null, location: formData.location||null, start_date: formData.start_date, end_date: formData.end_date, block_pay: blockPay, blocks_per_week: parseInt(formData.blocks_per_week)||3, notes: formData.notes ? `${formData.notes} (Tractor ${i+1}/${numTractors})` : `Tractor ${i+1}/${numTractors}` };
          await scheduleApi.create(payload);
        }
        toast.success(`${numTractors} contracts created (one per tractor)`);
      } else {
        // Single entry — check conflict
        if (formData.driver_id) {
          const conflict = getConflict(formData.driver_id, formData.start_date, formData.end_date);
          if (conflict) {
            const cLabel = conflict.contract_id || conflict.notes || conflict.entry_type;
            toast.error(`${driverMap[formData.driver_id]?.name || 'Driver'} is unavailable — conflicts with "${cLabel}" (${conflict.start_date} to ${conflict.end_date})`);
            return;
          }
        }
        const payload = { driver_id: formData.driver_id||null, entry_type: entryType, contract_id: entryType==='contract'?(formData.contract_id||null):null, location: entryType==='contract'?(formData.location||null):null, start_date: formData.start_date, end_date: formData.end_date, block_pay: entryType==='contract'?blockPay:0, blocks_per_week: entryType==='contract'?(parseInt(formData.blocks_per_week)||3):1, notes: formData.notes||null };
        await scheduleApi.create(payload);
        toast.success(entryType==='timeoff'?'Time off added':entryType==='load'?'Load added':'Contract added');
      }
      setDialogOpen(false); resetForm(); loadData();
    } catch(err) { toast.error(err.response?.data?.detail||'Failed'); }
  };
  const handleDelete = async (id) => { if(!window.confirm('Are you sure you want to remove this from the schedule? This cannot be undone.')) return; try{await scheduleApi.delete(id);toast.success('Removed');loadData();}catch{toast.error('Failed');} };
  const handleEdit = (entry) => {
    setEditingEntry(entry); setDialogTab(entry.entry_type==='load'?'load':entry.entry_type==='timeoff'?'timeoff':'contract');
    setFormData({ driver_id:entry.driver_id||'', entry_type:entry.entry_type||'contract', contract_id:entry.contract_id||'', location:entry.location||'', start_date:entry.start_date, end_date:entry.end_date, block_pay:entry.block_pay?.toString()||'', weekly_total:'', blocks_per_week:entry.blocks_per_week?.toString()||'3', notes:entry.notes||'' });
    setDialogOpen(true);
  };
  const handleDuplicate = (entry) => {
    setEditingEntry(null); setDialogTab(entry.entry_type==='load'?'load':'contract');
    setFormData({ driver_id:'', entry_type:entry.entry_type||'contract', contract_id:entry.contract_id||'', location:entry.location||'', start_date:entry.start_date, end_date:entry.end_date, block_pay:entry.block_pay?.toString()||'', weekly_total:'', blocks_per_week:entry.blocks_per_week?.toString()||'3', notes:entry.notes||'' });
    setDialogOpen(true);
  };
  const handleAdd = (driverId='', type='contract') => { resetForm(); setDialogTab(type); setFormData(p=>({...p,driver_id:driverId,entry_type:type})); setDialogOpen(true); };
  const resetForm = () => { setEditingEntry(null); setDialogTab('contract'); setFormData({driver_id:'',entry_type:'contract',contract_id:'',location:'',start_date:'',end_date:'',block_pay:'',weekly_total:'',blocks_per_week:'3',tractors:'1',tractor_drivers:[''],notes:''}); };

  /* ── Drag ──
   * `dragSegment` records the specific weekly slice the user grabbed: { entry, segStart, segEnd }.
   * If the user drops to a new driver and the slice is < the entry's full range, we call
   * `splitWeek` first so only THAT week moves to the new driver — the surrounding weeks stay
   * with the original driver.
   */
  const onDragStart = (e, entry, segStart, segEnd) => {
    // IMPORTANT: Do NOT call setDragEntry / setDragSegment here.
    // State updates trigger a re-render mid-dragstart which causes Chromium to cancel the
    // drag (the draggable element gets reconciled while the native drag is initializing,
    // which kills the drag session). Refs alone are sufficient — onDrop reads them.
    dragEntryRef.current = entry;
    dragSegmentRef.current = { entry, segStart, segEnd };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', entry.id);
  };
  const onDragOver = (e,tid) => { e.preventDefault(); setDragOver(tid); };
  const onDrop = async (e, tid) => {
    e.preventDefault();
    setDragOver(null);
    // Read from refs — survives stale-closure issues that pure React state alone hits.
    const entry = dragEntryRef.current;
    const seg = dragSegmentRef.current;
    dragSegmentRef.current = null;
    if (!entry) return;
    const nid = tid === 'unassigned' ? null : tid;
    if ((entry.driver_id || null) === nid) {
      dragEntryRef.current = null;
      return;
    }

    // Determine the date range that will actually move. If the user grabbed a single weekly
    // segment of a multi-week entry, split first so only that week shifts driver.
    const segStart = seg?.segStart || entry.start_date;
    const segEnd   = seg?.segEnd   || entry.end_date;
    const willSplit = segStart > entry.start_date || segEnd < entry.end_date;

    // Conflict check uses the actual moved range (not the whole multi-week contract)
    if (nid) {
      const conflict = getConflict(nid, segStart, segEnd, entry.id);
      if (conflict) {
        const cLabel = conflict.contract_id || conflict.notes || conflict.entry_type;
        toast.error(`${driverMap[nid]?.name || 'Driver'} is unavailable — conflicts with "${cLabel}" (${conflict.start_date} to ${conflict.end_date})`);
        dragEntryRef.current = null;
        return;
      }
    }

    try {
      let targetId = entry.id;
      if (willSplit) {
        const r = await scheduleApi.splitWeek(entry.id, segStart, segEnd);
        targetId = r.data.middle_id;
      }
      await scheduleApi.update(targetId, { driver_id: nid });
      toast.success(
        willSplit
          ? (nid ? `Week of ${segStart} reassigned` : 'Week unassigned')
          : (nid ? 'Assigned' : 'Unassigned')
      );
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    dragEntryRef.current = null;
  };

  const shiftW = (n) => setViewStart(p => addDays(p, n*7));
  const goToday = () => { const s = addDays(getSunday(localToday()), -7); setViewStart(s); setTimeout(()=>{if(scrollRef.current) scrollRef.current.scrollLeft=Math.max(0,daysBetween(s,localToday())*DW-200);},100); };

  /* ── Break entry into weekly segments for rendering ── */
  const getWeeklySegments = (entry) => {
    const segments = [];
    const entryStart = entry.start_date;
    const entryEnd = entry.end_date;

    for (const wk of weeks) {
      // Does entry overlap this week?
      if (entryEnd < wk.start || entryStart > wk.end) continue;
      const segStart = entryStart > wk.start ? entryStart : wk.start;
      const segEnd = entryEnd < wk.end ? entryEnd : wk.end;
      const leftDays = daysBetween(viewStart, segStart);
      const dur = daysBetween(segStart, segEnd) + 1;
      const left = leftDays * DW;
      const width = dur * DW;
      if (left + width <= 0 || left >= totalDays * DW) continue;
      segments.push({ left: Math.max(0, left), width: Math.max(DW, Math.min(width, totalDays*DW - Math.max(0,left))), segStart, segEnd });
    }
    return segments;
  };

  const todayLeft = daysBetween(viewStart, todayYMD) * DW + DW/2;

  /* ── Render a single weekly segment of a block ── */
  const renderSegment = (entry, seg, color, isFirst) => {
    const isLoad = entry.entry_type === 'load';
    const isTimeoff = entry.entry_type === 'timeoff';
    const cl = isTimeoff ? TIMEOFF_CL : (isLoad ? LOAD_CL : color);
    const driver = entry.driver_id ? driverMap[entry.driver_id] : null;
    const driverName = driver ? driver.name : '';
    const wks = wkSpan(entry.start_date, entry.end_date);
    const wkPay = (entry.block_pay||0) * (entry.blocks_per_week||3);
    const chainInfo = chainMap.get(entry.id);
    // Show "Week X of Y" badge on the first segment ONLY when the entry is part of a
    // multi-piece chain (i.e. has been split, or there are several adjacent entries with
    // the same contract identity).
    const showChainBadge = isFirst && chainInfo && chainInfo.chainTotalWeeks > 1 && chainInfo.chainLength > 1;

    return (
      <div
        key={`${entry.id}-${seg.segStart}`}
        draggable onDragStart={(e) => onDragStart(e, entry, seg.segStart, seg.segEnd)}
        className="absolute top-1 bottom-1 rounded-md cursor-grab active:cursor-grabbing overflow-hidden hover:shadow-lg group select-none z-10 flex items-center"
        style={{ left: seg.left, width: seg.width, backgroundColor: cl.bg, border: `2px solid ${cl.border}`, borderStyle: isLoad ? 'dashed' : isTimeoff ? 'dashed' : 'solid' }}
        data-testid={isFirst ? `schedule-entry-${entry.id}` : undefined}
      >
        <div className="flex flex-col justify-center px-2 py-0.5 min-w-0 w-full h-full" style={{ color: cl.text }}>
          {/* Location + date range */}
          <div className="flex items-center gap-1 leading-tight">
            {entry.location && <><MapPin className="h-3 w-3 flex-shrink-0" style={{color:cl.text}} /><span className="text-xs font-bold truncate">{entry.location}</span></>}
            <span className="text-[10px] opacity-70 ml-auto flex-shrink-0 whitespace-nowrap">{fmtShort(entry.start_date)}–{fmtShort(entry.end_date)}</span>
          </div>
          {/* ID + driver + actions */}
          <div className="flex items-center gap-1 leading-tight">
            <GripVertical className="h-3 w-3 opacity-40 flex-shrink-0" />
            <span className="text-[11px] font-semibold truncate">{isTimeoff?(entry.notes||'Time Off'):isLoad?(entry.notes||'Load'):(entry.contract_id||'No ID')}</span>
            {driverName && <span className="text-[10px] opacity-60 truncate hidden sm:inline">· {driverName}</span>}
            <div className="ml-auto flex items-center flex-shrink-0">
              <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();e.preventDefault();handleEdit(entry);}} className="p-0.5 rounded hover:bg-black/10 z-20"><Pencil className="h-3 w-3" /></button>
              <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();e.preventDefault();handleDuplicate(entry);}} className="p-0.5 rounded hover:bg-black/10 z-20" title="Duplicate"><Copy className="h-3 w-3" /></button>
              <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();e.preventDefault();handleDelete(entry.id);}} className="p-0.5 rounded hover:bg-red-200 z-20" style={{color:'#DC2626'}}><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
          {/* Block pay */}
          {!isLoad && entry.block_pay > 0 && (
            <div className="flex items-baseline gap-1 leading-tight">
              <span className="text-sm font-extrabold">${(entry.block_pay).toLocaleString('en-US')}/blk</span>
              {seg.width > 130 && <span className="text-[9px] opacity-60">${wkPay.toLocaleString('en-US')}/wk · {wks}wk</span>}
              {showChainBadge && seg.width > 90 && (
                <span className="text-[9px] font-bold ml-auto px-1 py-px rounded bg-black/15" title={`Part of a ${chainInfo.chainTotalWeeks}-week contract`}>
                  Wk {chainInfo.weekStartIndex}{chainInfo.weekStartIndex !== chainInfo.weekEndIndex ? `–${chainInfo.weekEndIndex}` : ''}/{chainInfo.chainTotalWeeks}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ── Timeline row ── */
  const TimelineRow = ({ driverEntries, colorIdx, targetId, dimmed }) => (
    <div
      className={`border-b relative transition-colors ${dragOver===targetId?'bg-blue-50/70 dark:bg-blue-950/30':''} ${dimmed?'opacity-30':''}`}
      style={{ height: RH }}
      onDragOver={e=>onDragOver(e,targetId)} onDragLeave={()=>setDragOver(null)} onDrop={e=>onDrop(e,targetId)}
      data-testid={`schedule-row-${targetId}`}
    >
      {/* Week grid lines */}
      {weeks.map((_,i)=><div key={i} className="absolute top-0 bottom-0 border-r border-dashed border-muted/40" style={{left:(i+1)*7*DW}} />)}
      {/* Today line */}
      {todayLeft>=0 && todayLeft<=totalDays*DW && <div className="absolute top-0 bottom-0 w-[2px] bg-red-500/20 z-[5] pointer-events-none" style={{left:todayLeft}} />}
      {/* Segments */}
      {driverEntries.map(entry => {
        const segs = getWeeklySegments(entry);
        const color = CL[(colorIdx >= 0 ? colorIdx : 0) % CL.length];
        return segs.map((seg, si) => renderSegment(entry, seg, color, si === 0));
      })}
    </div>
  );

  if (loading) return <div className="space-y-6" data-testid="schedule-loading"><Skeleton className="h-8 w-48" /><Card><CardContent className="p-6"><Skeleton className="h-[400px] w-full" /></CardContent></Card></div>;

  const sideW = sidebarOpen ? 200 : 48;

  return (
    <div className="space-y-3 animate-fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-manrope font-bold">Driver Schedule</h1>
          <p className="text-muted-foreground text-sm">Sun–Sat · Eastern Time</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button variant={showVacancies?'default':'outline'} size="sm" onClick={()=>setShowVacancies(!showVacancies)} data-testid="vacancies-btn" className={showVacancies?'bg-slate-700 hover:bg-slate-800':''}>
            <Package className="h-4 w-4 mr-1" />Vacancies{unassigned.length>0&&<Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">{unassigned.length}</Badge>}
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="schedule-today-btn"><Calendar className="h-4 w-4 mr-1" />Today</Button>
          <div className="flex items-center border rounded-md">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={()=>shiftW(-2)} data-testid="schedule-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium px-2 min-w-[140px] text-center">{fmtShort(weeks[0]?.start)} – {fmtShort(weeks[weeks.length-1]?.end)}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={()=>shiftW(2)} data-testid="schedule-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Select value={driverFilter} onValueChange={(v) => { setDriverFilter(v); setFocusedDriver(null); }}>
            <SelectTrigger className="h-8 w-[175px] text-xs" data-testid="schedule-driver-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active drivers</SelectItem>
              <SelectItem value="inactive">Inactive / on leave ({inactiveCount})</SelectItem>
              <SelectItem value="all">All drivers</SelectItem>
            </SelectContent>
          </Select>
          <Select value={viewWeeks.toString()} onValueChange={v=>setViewWeeks(parseInt(v))}>
            <SelectTrigger className="h-8 w-[90px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{[2,4,6,8,12,16,24].map(n=><SelectItem key={n} value={n.toString()}>{n} Wks</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={()=>handleAdd()} data-testid="add-contract-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
          <Select value="print" onValueChange={async (v) => {
            if (v === 'print') return;
            try {
              const driverId = v === 'all' ? null : v;
              const res = await scheduleApi.exportPdf(driverId);
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url;
              a.download = driverId ? `schedule_${drivers.find(d=>d.id===driverId)?.name?.replace(/\s/g,'_')||'driver'}.pdf` : 'schedule_all.pdf';
              a.click(); window.URL.revokeObjectURL(url);
              toast.success('PDF downloaded');
            } catch { toast.error('Failed to export PDF'); }
          }}>
            <SelectTrigger className="h-8 w-[100px] text-xs" data-testid="print-btn"><Printer className="h-3 w-3 mr-1" /><span>Print</span></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Drivers</SelectItem>
              {visibleDrivers.map(d=><SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const res = await scheduleApi.exportPdf(focusedDriver);
              const blob = new Blob([res.data], { type: 'application/pdf' });
              const file = new File([blob], 'schedule.pdf', { type: 'application/pdf' });
              if (navigator.share && navigator.canShare({ files: [file] })) {
                await navigator.share({ title: 'Driver Schedule', files: [file] });
              } else {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'schedule.pdf'; a.click();
                window.URL.revokeObjectURL(url);
                toast.success('PDF downloaded — share from your device');
              }
            } catch { toast.error('Failed to share'); }
          }} data-testid="share-btn"><Share2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Vacancies Panel */}
      {showVacancies && (
        <Card data-testid="vacancies-panel"><CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold flex items-center gap-1.5"><Package className="h-4 w-4" />Contract Vacancies<Badge variant="secondary" className="text-[10px]">{unassigned.length}</Badge></h3>
            <Button variant="ghost" size="sm" onClick={()=>handleAdd('','contract')} className="text-xs"><Plus className="h-3 w-3 mr-1" />Add</Button>
          </div>
          {unassigned.length===0?<p className="text-sm text-muted-foreground text-center py-3">No vacancies</p>:(
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {unassigned.map(entry=>(
                <div key={entry.id} draggable onDragStart={e=>onDragStart(e,entry)} className="p-2 rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 cursor-grab hover:shadow-md" data-testid={`vacancy-${entry.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 min-w-0">
                      {entry.location?<><MapPin className="h-3 w-3 text-slate-500 flex-shrink-0" /><span className="text-sm font-bold truncate">{entry.location}</span></>:<span className="text-sm text-muted-foreground">No location</span>}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button onClick={()=>handleEdit(entry)} className="p-1 rounded hover:bg-slate-200"><Pencil className="h-3 w-3" /></button>
                      <button onClick={()=>handleDuplicate(entry)} className="p-1 rounded hover:bg-slate-200"><Copy className="h-3 w-3" /></button>
                      <button onClick={()=>handleDelete(entry.id)} className="p-1 rounded hover:bg-red-100 text-red-500"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{entry.contract_id||'No ID'} · {fmtShort(entry.start_date)} – {fmtShort(entry.end_date)}</div>
                  {entry.block_pay>0&&<div className="text-sm font-bold mt-0.5">${entry.block_pay.toLocaleString('en-US')}/block</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Gantt Chart */}
      <Card className="overflow-hidden">
        <div className="flex">
          {/* Driver sidebar */}
          <div className="flex-shrink-0 border-r bg-muted/30 z-10 transition-all" style={{width: sideW}}>
            {/* Sidebar header */}
            <div className="h-[60px] border-b flex items-center px-2 sticky top-0 bg-muted/30 z-20 gap-1">
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={()=>setSidebarOpen(!sidebarOpen)} data-testid="toggle-sidebar">
                {sidebarOpen?<PanelLeftClose className="h-4 w-4" />:<PanelLeft className="h-4 w-4" />}
              </Button>
              {sidebarOpen && <><span className="text-xs font-semibold text-muted-foreground uppercase">Drivers</span><Badge variant="secondary" className="ml-auto text-[10px]" data-testid="schedule-driver-count">{visibleDrivers.length}</Badge></>}
            </div>
            {visibleDrivers.map((driver,di) => {
              const isFocused = focusedDriver === driver.id;
              return (
                <div key={driver.id}
                  className={`border-b flex items-center px-2 gap-1 transition-colors cursor-pointer ${dragOver===driver.id?'bg-blue-50 dark:bg-blue-950/30':''} ${isFocused?'bg-blue-100 dark:bg-blue-900/40 ring-2 ring-blue-400 ring-inset':''} ${focusedDriver && !isFocused?'opacity-40':''}`}
                  style={{ height: RH }}
                  onClick={()=>setFocusedDriver(focusedDriver===driver.id?null:driver.id)}
                  onDragOver={e=>onDragOver(e,driver.id)} onDragLeave={()=>setDragOver(null)} onDrop={e=>onDrop(e,driver.id)}
                  data-testid={`schedule-driver-${driver.id}`}
                >
                  {sidebarOpen ? (
                    <>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium truncate block">{driver.name}</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {driver.status !== 'active' && <span className="text-[9px] px-1.5 py-px rounded font-semibold uppercase bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200" data-testid={`schedule-driver-status-${driver.id}`}>{driver.status === 'on_leave' ? 'On leave' : 'Inactive'}</span>}
                          {driver.unit_number&&<span className="text-[10px] flex items-center gap-0.5 text-muted-foreground"><Truck className="h-3 w-3" />{driver.unit_number}</span>}
                          {driver.phone&&<span className="text-[10px] flex items-center gap-0.5 text-muted-foreground"><Phone className="h-3 w-3" />{driver.phone}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-0.5 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-5 w-5 opacity-50 hover:opacity-100" onClick={e=>{e.stopPropagation();handleAdd(driver.id,'contract');}} data-testid={`add-contract-driver-${driver.id}`}><Plus className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-5 w-5 opacity-50 hover:opacity-100" onClick={e=>{e.stopPropagation();handleAdd(driver.id,'load');}} data-testid={`add-load-driver-${driver.id}`}><Package className="h-3 w-3" /></Button>
                      </div>
                    </>
                  ) : (
                    <span className="text-xs font-bold text-center w-full" title={driver.name}>{driver.name.split(' ').map(n=>n[0]).join('')}</span>
                  )}
                </div>
              );
            })}
            {visibleDrivers.length===0&&<div className="p-4 text-center text-muted-foreground text-xs" data-testid="schedule-no-drivers"><User className="h-5 w-5 mx-auto mb-1 opacity-50" />{driverFilter==='active'?'No active drivers':driverFilter==='inactive'?'No inactive drivers':'No drivers'}</div>}
          </div>

          {/* Timeline */}
          <div className="flex-1 overflow-x-auto" ref={scrollRef}>
            <div style={{width:totalDays*DW, minWidth:'100%'}}>
              {/* Sticky header */}
              <div className="h-[60px] border-b flex relative sticky top-0 bg-background z-20">
                {weeks.map((wk,i)=>(
                  <div key={i} className="flex-shrink-0 border-r" style={{width:7*DW}}>
                    <div className="text-xs font-semibold text-foreground px-1.5 py-0.5 border-b bg-muted/30 text-center">{fmtShort(wk.start)} – {fmtShort(wk.end)}</div>
                    <div className="flex">
                      {DNAMES.map((lbl,di)=>{
                        const dayYMD = addDays(wk.start, di);
                        const num = parseD(dayYMD).getDate();
                        const isWe = di===0||di===6;
                        const isT = dayYMD === todayYMD;
                        return (
                          <div key={di} className={`flex flex-col items-center justify-center ${isWe?'bg-muted/30':''}`} style={{width:DW}}>
                            <span className={`text-[10px] font-medium leading-tight ${isT?'text-red-600 font-bold':isWe?'text-muted-foreground/50':'text-muted-foreground'}`}>{lbl}</span>
                            <span className={`text-xs font-semibold leading-tight ${isT?'bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center':isWe?'text-muted-foreground/40':'text-foreground/80'}`}>{num}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {todayLeft>=0&&todayLeft<=totalDays*DW&&<div className="absolute top-0 bottom-0 w-[2px] bg-red-500 z-30 pointer-events-none" style={{left:todayLeft}} />}
              </div>

              {/* Driver rows */}
              {visibleDrivers.map((driver,di)=>(
                <TimelineRow key={driver.id} driverEntries={entries.filter(e=>e.driver_id===driver.id)} colorIdx={di} targetId={driver.id} dimmed={focusedDriver && focusedDriver!==driver.id} />
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={o=>{setDialogOpen(o);if(!o)resetForm();}}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-600" />{editingEntry?'Edit Entry':'Add to Schedule'}</DialogTitle>
            <DialogDescription>{editingEntry?'Update details':'Add a contract or load'}</DialogDescription>
          </DialogHeader>
          {!editingEntry&&(<Tabs value={dialogTab} onValueChange={setDialogTab}><TabsList className="grid w-full grid-cols-3"><TabsTrigger value="contract" data-testid="tab-contract">Contract</TabsTrigger><TabsTrigger value="load" data-testid="tab-load">Load</TabsTrigger><TabsTrigger value="timeoff" data-testid="tab-timeoff">Time Off</TabsTrigger></TabsList></Tabs>)}
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Driver select — hidden for multi-tractor contracts (uses per-tractor assignment instead) */}
            {(parseInt(formData.tractors) <= 1 || editingEntry || dialogTab !== 'contract') && (
            <div className="space-y-1.5"><Label>Driver</Label>
              <Select value={formData.driver_id||'unassigned'} onValueChange={v=>setFormData({...formData,driver_id:v==='unassigned'?'':v})}>
                <SelectTrigger data-testid="schedule-driver-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned"><div className="flex items-center gap-2 text-slate-500"><Package className="h-3 w-3" />Unassigned</div></SelectItem>
                  {drivers.filter(d => d.status === 'active' || d.id === formData.driver_id).map(d => {
                    const conflict = (formData.start_date && formData.end_date)
                      ? getConflict(d.id, formData.start_date, formData.end_date, editingEntry?.id)
                      : null;
                    return (
                      <SelectItem key={d.id} value={d.id} disabled={!!conflict}>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3" />
                          <span>{d.name}</span>
                          {d.unit_number && <span className="text-muted-foreground text-xs">({d.unit_number})</span>}
                          {conflict && <span className="text-red-600 text-[10px] ml-1">· busy {fmtShort(conflict.start_date)}–{fmtShort(conflict.end_date)}</span>}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            )}
            {(editingEntry ? editingEntry.entry_type === 'contract' : dialogTab === 'contract') && (
              <div className="space-y-1.5"><Label>Location / Domicile</Label><Input value={formData.location} onChange={e=>setFormData({...formData,location:e.target.value})} placeholder="e.g., Columbus, OH" data-testid="schedule-location-input" /></div>
            )}
            {(editingEntry ? editingEntry.entry_type === 'contract' : dialogTab === 'contract') && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Contract ID</Label><Input value={formData.contract_id} onChange={e=>setFormData({...formData,contract_id:e.target.value})} placeholder="C-0002XJ1MP" data-testid="schedule-contract-input" /></div>
                <div className="space-y-1.5"><Label>Block Pay ($)</Label><Input type="number" step="0.01" min="0" value={formData.block_pay} onChange={e=>{
                  const bp = e.target.value;
                  const bpw = parseInt(formData.blocks_per_week)||3;
                  const t = parseInt(formData.tractors)||1;
                  setFormData({...formData, block_pay: bp, weekly_total: bp ? (parseFloat(bp)*bpw*t).toFixed(2) : ''});
                }} placeholder="Per tractor" data-testid="schedule-blockpay-input" /></div>
                <div className="space-y-1.5"><Label>Weekly Total ($)</Label><Input type="number" step="0.01" min="0" value={formData.weekly_total} onChange={e=>{
                  const wt = e.target.value;
                  const bpw = parseInt(formData.blocks_per_week)||3;
                  const t = parseInt(formData.tractors)||1;
                  setFormData({...formData, weekly_total: wt, block_pay: wt ? (parseFloat(wt)/t/bpw).toFixed(2) : ''});
                }} placeholder="All tractors" data-testid="schedule-weeklytotal-input" /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start Date *</Label><Input type="date" value={formData.start_date} onChange={e=>setFormData({...formData,start_date:e.target.value})} required data-testid="schedule-start-input" /></div>
              <div className="space-y-1.5"><Label>End Date *</Label><Input type="date" value={formData.end_date} onChange={e=>setFormData({...formData,end_date:e.target.value})} required data-testid="schedule-end-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {(editingEntry?editingEntry.entry_type==='contract':dialogTab==='contract')&&(
                <>
                <div className="space-y-1.5"><Label>Blocks/Wk</Label><Select value={formData.blocks_per_week} onValueChange={v=>{
                  const bpw = parseInt(v)||3;
                  const bp = parseFloat(formData.block_pay)||0;
                  const t = parseInt(formData.tractors)||1;
                  setFormData({...formData, blocks_per_week:v, weekly_total: bp ? (bp*bpw*t).toFixed(2) : ''});
                }}><SelectTrigger data-testid="schedule-blocks-select"><SelectValue /></SelectTrigger><SelectContent>{[1,2,3,4,5].map(n=><SelectItem key={n} value={n.toString()}>{n}/wk</SelectItem>)}</SelectContent></Select></div>
                {!editingEntry && (
                  <div className="space-y-1.5"><Label>Tractors</Label><Select value={formData.tractors} onValueChange={v=>{
                    const t = parseInt(v)||1;
                    const bpw = parseInt(formData.blocks_per_week)||3;
                    // If weekly total was entered, recalc block pay from total
                    const wt = parseFloat(formData.weekly_total)||0;
                    if (wt) {
                      setFormData({...formData, tractors:v, tractor_drivers: Array(t).fill(''), block_pay: (wt/t/bpw).toFixed(2)});
                    } else {
                      const bp = parseFloat(formData.block_pay)||0;
                      setFormData({...formData, tractors:v, tractor_drivers: Array(t).fill(''), weekly_total: bp ? (bp*bpw*t).toFixed(2) : ''});
                    }
                  }}><SelectTrigger data-testid="schedule-tractors-select"><SelectValue /></SelectTrigger><SelectContent>{[1,2,3,4,5].map(n=><SelectItem key={n} value={n.toString()}>{n} truck{n>1?'s':''}</SelectItem>)}</SelectContent></Select></div>
                )}
                </>
              )}
              <div className={`space-y-1.5 ${(editingEntry?editingEntry.entry_type!=='contract':dialogTab!=='contract')?'col-span-3':''}`}>
                <Label>{(editingEntry?editingEntry.entry_type==='timeoff':dialogTab==='timeoff')?'Reason':(editingEntry?editingEntry.entry_type==='load':dialogTab==='load')?'Description':'Notes'}</Label>
                <Input value={formData.notes} onChange={e=>setFormData({...formData,notes:e.target.value})} placeholder={(editingEntry?editingEntry.entry_type==='timeoff':dialogTab==='timeoff')?'PTO, Sick, Appointment, etc.':(editingEntry?editingEntry.entry_type==='load':dialogTab==='load')?'Gap fill to Detroit':'Optional'} data-testid="schedule-notes-input" />
              </div>
            </div>
            {/* Per-tractor driver assignment when tractors > 1 */}
            {!editingEntry && (editingEntry ? editingEntry.entry_type === 'contract' : dialogTab === 'contract') && parseInt(formData.tractors) > 1 && (
              <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
                <Label className="text-xs font-semibold">Assign Drivers per Tractor</Label>
                {Array.from({ length: parseInt(formData.tractors) || 1 }, (_, i) => {
                  // Drivers already chosen on other tractor rows in this same submission
                  const chosenElsewhere = (formData.tractor_drivers || [])
                    .filter((d, idx) => d && idx !== i);
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-16 flex-shrink-0">Truck {i + 1}</span>
                      <Select value={formData.tractor_drivers[i] || 'unassigned'} onValueChange={v => {
                        const updated = [...formData.tractor_drivers];
                        updated[i] = v === 'unassigned' ? '' : v;
                        setFormData({ ...formData, tractor_drivers: updated });
                      }}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`tractor-driver-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned"><span className="text-slate-500">Unassigned</span></SelectItem>
                          {drivers.filter(d => d.status === 'active').map(d => {
                            const conflict = (formData.start_date && formData.end_date)
                              ? getConflict(d.id, formData.start_date, formData.end_date)
                              : null;
                            const dupe = chosenElsewhere.includes(d.id);
                            const disabled = !!conflict || dupe;
                            return (
                              <SelectItem key={d.id} value={d.id} disabled={disabled}>
                                {d.name}{d.unit_number && ` (${d.unit_number})`}
                                {conflict && <span className="text-red-600 text-[10px] ml-1">· busy</span>}
                                {!conflict && dupe && <span className="text-amber-600 text-[10px] ml-1">· already chosen</span>}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            )}
            {(editingEntry?editingEntry.entry_type==='contract':dialogTab==='contract')&&(formData.block_pay||formData.weekly_total)&&formData.start_date&&formData.end_date&&(
              <div className="p-2.5 rounded-lg bg-muted/50 border text-sm" data-testid="schedule-pay-summary">
                {parseInt(formData.tractors) > 1 && (
                  <div className="flex justify-between mb-1 pb-1 border-b"><span className="text-muted-foreground text-xs">Total ({formData.tractors} tractors)</span><span className="font-semibold">${parseFloat(formData.weekly_total||0).toLocaleString('en-US')}/wk</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground text-xs">{parseInt(formData.tractors)>1?'Per Tractor Block':'Block'}</span><span className="font-bold text-base">${parseFloat(formData.block_pay||0).toLocaleString('en-US')}/block</span></div>
                <div className="flex justify-between mt-0.5"><span className="text-muted-foreground text-xs">{parseInt(formData.tractors)>1?'Per Tractor Weekly':'Weekly'} ({formData.blocks_per_week}×)</span><span className="font-semibold">${(parseFloat(formData.block_pay||0)*parseInt(formData.blocks_per_week||3)).toLocaleString('en-US')}/wk</span></div>
                <div className="flex justify-between mt-0.5 pt-0.5 border-t"><span className="text-muted-foreground text-xs">{parseInt(formData.tractors)>1?`Grand Total (${formData.tractors}× ${wkSpan(formData.start_date,formData.end_date)}wk)`:`Total (${wkSpan(formData.start_date,formData.end_date)}wk)`}</span><span className="text-muted-foreground">${(parseFloat(formData.block_pay||0)*parseInt(formData.blocks_per_week||3)*wkSpan(formData.start_date,formData.end_date)*(parseInt(formData.tractors)||1)).toLocaleString('en-US')}</span></div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={!formData.start_date||!formData.end_date||((editingEntry?editingEntry.entry_type==='contract':dialogTab==='contract')&&!formData.block_pay&&!formData.weekly_total)} data-testid="schedule-submit-btn">
                {editingEntry?'Update':dialogTab==='timeoff'?'Add Time Off':dialogTab==='load'?'Add Load':'Add Contract'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
